import React, { useState, useMemo, useEffect } from 'react';
import {
    Search,
    Filter,
    ChevronUp,
    ChevronDown,
    Edit,
    Trash2,
    Eye,
    ArrowUpDown
} from 'lucide-react';
import { useTranslation } from '../i18n/i18n';

// Composant Tableau Réutilisable
const ReusableTable = ({
    data = [],
    columns = [],
    onEdit,
    onDelete,
    onDetails,
    onRowClick,
    colors = {},
    lang,
    itemsPerPageOptions = [10, 25, 50, 100]
}) => {
    const { t } = useTranslation(lang);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [filterColumn, setFilterColumn] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(itemsPerPageOptions[0]);
    // Couleurs par défaut avec possibilité de personnalisation
    const themeColors = {
        headerBg: colors.headerBg || '#2c3e50',
        headerText: colors.headerText || '#ffffff',
        rowEven: colors.rowEven || '#ffffff',
        rowOdd: colors.rowOdd || '#f8f9fa',
        rowHover: colors.rowHover || '#e3f2fd',
        border: colors.border || '#d1e8f3ff',
        searchBg: colors.searchBg || '#ffffff',
        buttonEdit: colors.buttonEdit || '#3498db',
        buttonDelete: colors.buttonDelete || '#e74c3c',
        buttonDetails: colors.buttonDetails || '#2ecc71',
        expandedBg: colors.expandedBg || '#f0f8ff',
        paginatebg: colors.paginatebg || '#edeafdff'
    };

    // Filtrage et recherche
    const filteredData = useMemo(() => {
        return data.filter(item => {
            const searchValue = searchTerm.toLowerCase();

            if (filterColumn === 'all') {
                return columns.some(col =>
                    String(item[col.key]).toLowerCase().includes(searchValue)
                );
            } else {
                return String(item[filterColumn]).toLowerCase().includes(searchValue);
            }
        });
    }, [data, searchTerm, filterColumn, columns]);

    // Tri
    const sortedData = useMemo(() => {
        let sortableItems = [...filteredData];

        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Tente de convertir en nombre si ce n'est pas une chaîne
                if (typeof aValue !== 'string' || (typeof aValue === 'string' && !isNaN(aValue))) {
                    aValue = parseFloat(aValue) || 0;
                }
                if (typeof bValue !== 'string' || (typeof bValue === 'string' && !isNaN(bValue))) {
                    bValue = parseFloat(bValue) || 0;
                }

                // Gérer les chaînes composées (ex: "125.50 EUR")
                if (typeof aValue === 'string' && (aValue.includes('€') || aValue.includes('EUR'))) {
                    aValue = parseFloat(aValue.replace(/[^0-9.-]+/g, "")) || 0;
                }
                if (typeof bValue === 'string' && (bValue.includes('€') || bValue.includes('EUR'))) {
                    bValue = parseFloat(bValue.replace(/[^0-9.-]+/g, "")) || 0;
                }

                if (typeof aValue === 'string' && (aValue.includes('%'))) {
                    aValue = parseFloat(aValue.replace(/[^0-9.-]+/g, "")) || 0;
                }
                if (typeof bValue === 'string' && (bValue.includes('%'))) {
                    bValue = parseFloat(bValue.replace(/[^0-9.-]+/g, "")) || 0;
                }

                // Si les valeurs sont des nombres, on compare directement
                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    if (aValue < bValue) {
                        return sortConfig.direction === 'asc' ? -1 : 1;
                    }
                    if (aValue > bValue) {
                        return sortConfig.direction === 'asc' ? 1 : -1;
                    }
                    return 0;
                }

                // Si les valeurs sont des chaînes (après normalisation pour les cas non numériques), on compare en minuscule
                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                }
                if (typeof bValue === 'string') {
                    bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return sortableItems;
    }, [filteredData, sortConfig]);

    // Gestion du tri
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const styles = {
        container: {
            padding: '10px',
            maxWidth: '100%'
            // overflowX: 'auto'
        },
        controlsBar: {
            display: 'flex',
            gap: '15px',
            marginBottom: '20px',
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        searchContainer: {
            position: 'relative',
            flex: '1',
            maxWidth: '800px'
        },
        searchInput: {
            padding: '10px 40px 10px 15px',
            border: '1px',
            borderStyle: 'none',
            borderColor: `${themeColors.border}`,
            borderRadius: '6px',
            fontSize: '14px',
            width: '100%',
            backgroundColor: themeColors.searchBg,
            boxSizing: 'border-box'
        },
        searchIcon: {
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#6b7280'
        },
        filtersContainer: {
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            minWidth: '200px'
        },
        select: {
            padding: '10px 12px',
            border: '1px',
            borderStyle: 'none',
            borderColor: `${themeColors.border}`,
            borderRadius: '6px',
            fontSize: '14px',
            backgroundColor: themeColors.searchBg,
            cursor: 'pointer',
            minWidth: '150px'
        },
        table: {
            width: '100%',
            minWidth: '1000px',
            borderCollapse: 'collapse',
            backgroundColor: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            overflow: 'hidden',
            fontSize: '14px' // Taille de police réduite
        },
        th: {
            backgroundColor: themeColors.headerBg,
            color: themeColors.headerText,
            padding: '12px 10px', // Padding réduit
            justifyContent: 'center',
            textAlign: 'center',
            fontWeight: '600',
            cursor: 'pointer',
            userSelect: 'none',
            borderBottom: '1px',
            borderStyle: 'none',
            borderColor: `${themeColors.border}`,
            whiteSpace: 'nowrap',
            fontSize: '14px'
        },
        td: {
            padding: '12px 10px', // Padding réduit
            borderBottom: '1px',
            borderStyle: 'none',
            borderColor: `${themeColors.border}`,
            fontSize: '14px',
            textAlign: 'center'
        },
        actionButtons: {
            display: 'flex',
            gap: '6px',
            flexWrap: 'nowrap',
            zIndex: 1000,
            justifyContent: 'flex-start'
        },
        button: {
            padding: '6px 10px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            color: '#fff',
            transition: 'all 0.2s ease',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',

            minWidth: 'auto'
        },
        iconButton: {
            padding: '4px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px'
        },
        expandedRow: {
            backgroundColor: themeColors.expandedBg,
            padding: '15px'
        },
        detailsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '10px'
        },
        detailItem: {
            padding: '8px',
            backgroundColor: '#fff',
            borderRadius: '4px',
            border: '1px',
            borderStyle: 'none',
            borderColor: `${themeColors.border}`
        },
        detailLabel: {
            fontWeight: '600',
            color: '#555',
            fontSize: '11px',
            marginBottom: '1px',
            textTransform: 'uppercase'
        },
        detailValue: {
            color: '#333',
            fontSize: '12px',
            fontWeight: '500'
        },
        noData: {
            textAlign: 'center',
            padding: '30px',
            color: '#999',
            fontSize: '14px',
            fontStyle: 'italic'
        },
        sortIndicator: {
            marginLeft: '4px',
            display: 'flex',
            alignItems: 'center'
        },
        tableWrapper: {
            overflowX: 'auto',
            borderRadius: '8px',
            marginBottom: '10px'
        },
        pagination: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '20px',
            padding: '16px',
            backgroundColor: `${themeColors.paginatebg}`,
            borderRadius: '8px',
            border: '1px',
            borderStyle: 'none',
            borderColor: `${themeColors.border}`,

            flexWrap: 'wrap',
            gap: '15px'
        },
        paginationControls: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap'
        },
        paginationNavigation: {
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            flexWrap: 'wrap'
        },
        paginationText: {
            fontSize: '14px',
            color: '#6b7280'
        },
        paginationSelect: {
            padding: '6px 10px',
            border: '1px',
            borderStyle: 'none',
            borderColor: `${themeColors.border}`,
            borderRadius: '4px',
            fontSize: '14px'
        },
        paginationButton: {
            padding: '8px 12px',
            border: '1px',
            borderStyle: 'none',
            borderColor: `${themeColors.border}`,
            backgroundColor: 'white',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            minWidth: '40px'
        },
        paginationButtonActive: {
            backgroundColor: themeColors.headerBg,
            color: 'white',
            borderColor: themeColors.headerBg
        },
        paginationButtonDisabled: {
            backgroundColor: '#f3f4f6',
            color: '#9ca3af',
            cursor: 'not-allowed',
            borderColor: '#e5e7eb'
        }
    };

    const getRowStyle = (index) => ({
        backgroundColor: index % 2 === 0 ? themeColors.rowEven : themeColors.rowOdd,
        transition: 'background-color 0.2s',
        cursor: 'pointer'
    });

    // Rendu responsive conditionnel
    const isMobile = window.innerWidth < 768;

    // Pagination
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(sortedData.length / itemsPerPage);

    // Reset à la page 1 quand les données ou les filtres changent
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterColumn, sortConfig]);

    // Gestionnaires de pagination
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (value) => {
        setItemsPerPage(Number(value));
        setCurrentPage(1);
    };

    // Générer les numéros de page à afficher
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    return (
        <div style={styles.container}>
            {/* Barre de contrôle - Toujours sur la même ligne */}
            <div style={styles.controlsBar}>
                <div style={styles.searchContainer}>
                    <input
                        type="text"
                        placeholder={t('searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.searchInput}
                    />
                    <Search size={16} style={styles.searchIcon} />
                </div>

                <div style={styles.filtersContainer}>
                    <Filter size={14} color="#6b7280" />
                    <select
                        value={filterColumn}
                        onChange={(e) => setFilterColumn(e.target.value)}
                        style={styles.select}
                    >
                        <option value="all">{t('allColumns')}</option>
                        {columns.map(col => (
                            <option key={col.key} value={col.key}>
                                {col.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tableau */}
            <div style={styles.tableWrapper}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            {/* <th style={{...styles.th, width: '50px'}}>
                {t('expand') || "Expand"}
              </th> */}
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    style={styles.th}
                                    onClick={() => handleSort(col.key)}
                                >
                                    <div style={{
                                        display: 'flex', justifyContent: 'center',
                                        textAlign: 'center', gap: '4px'
                                    }}>
                                        <span>{col.label}</span>
                                        <div style={styles.sortIndicator}>
                                            {sortConfig.key === col.key ? (
                                                sortConfig.direction === 'asc' ?
                                                    <ChevronUp size={14} /> :
                                                    <ChevronDown size={14} />
                                            ) : (
                                                <ArrowUpDown size={12} opacity={0.5} />
                                            )}
                                        </div>
                                    </div>
                                </th>
                            ))}
                            <th style={{ ...styles.th, width: '120px' }}>
                                {t('actions')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length === 0 ? ( // Utilise paginatedData au lieu de sortedData
                            <tr>
                                <td colSpan={columns.length + 2} style={styles.noData}>
                                    {t('noData') || "Aucune donnée à afficher"}
                                </td>
                            </tr>
                        ) : (
                            paginatedData.map((row, index) => ( // Utilise paginatedData au lieu de sortedData
                                <React.Fragment key={index}>
                                    <tr
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.rowHover}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? themeColors.rowEven : themeColors.rowOdd}
                                        onClick={() => onRowClick && onRowClick(row)}
                                        style={{ ...getRowStyle(index), cursor: onRowClick ? 'pointer' : 'default' }}
                                    >
                                        {columns.map(col => (
                                            <td key={col.key} style={styles.td}>
                                                {col.render
                                                    ? col.render(row[col.key], row) // rendu stylisé
                                                    : row[col.key] // rendu par défaut
                                                }
                                            </td>
                                        ))}

                                        <td style={styles.td}>
                                            <div style={styles.actionButtons}>
                                                {onEdit && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Ajoute cette ligne
                                                            onEdit(row);
                                                        }}
                                                        style={{
                                                            ...styles.button,
                                                            backgroundColor: themeColors.buttonEdit
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                                                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                                                        title={t('edit')}
                                                    >
                                                        <Edit size={12} />
                                                        {!isMobile && (
                                                            <span>{t('edit')}</span>
                                                        )}
                                                    </button>
                                                )}
                                                {onDelete && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDelete(row);
                                                        }}
                                                        style={{
                                                            ...styles.button,
                                                            backgroundColor: themeColors.buttonDelete
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                                                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                                                        title={t('delete')}
                                                    >
                                                        <Trash2 size={12} />
                                                        {!isMobile && (
                                                            <span>{t('delete')}</span>
                                                        )}
                                                    </button>
                                                )}
                                                {onDetails && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDetails(row);
                                                        }}
                                                        style={{
                                                            ...styles.button,
                                                            backgroundColor: themeColors.buttonDetails
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                                                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                                                        title={t('details')}
                                                    >
                                                        <Eye size={12} />
                                                        {!isMobile && (
                                                            <span>{t('details')}</span>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>

                {/* Pagination */}
                {sortedData.length > 0 && (
                    <div style={styles.pagination}>
                        {/* Sélection du nombre d'éléments par page */}
                        <div style={styles.paginationControls}>
                            <span style={styles.paginationText}>
                                {t('itemsPerPage')}:
                            </span>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(e.target.value)}
                                style={styles.paginationSelect}
                            >
                                {itemsPerPageOptions.map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>

                            <span style={styles.paginationText}>
                                {t('showing')} {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, sortedData.length)} {t('of')} {sortedData.length} {t('items')}
                            </span>
                        </div>

                        {/* Navigation des pages */}
                        <div style={styles.paginationNavigation}>
                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(currentPage === 1 && styles.paginationButtonDisabled)
                                }}
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                            >
                                {t('first')}
                            </button>

                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(currentPage === 1 && styles.paginationButtonDisabled)
                                }}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                {t('previous')}
                            </button>

                            {/* Numéros de page */}
                            {getPageNumbers().map(page => (
                                <button
                                    key={page}
                                    style={{
                                        ...styles.paginationButton,
                                        ...(currentPage === page && styles.paginationButtonActive)
                                    }}
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </button>
                            ))}

                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(currentPage === totalPages && styles.paginationButtonDisabled)
                                }}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                {t('next')}
                            </button>

                            <button
                                style={{
                                    ...styles.paginationButton,
                                    ...(currentPage === totalPages && styles.paginationButtonDisabled)
                                }}
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                            >
                                {t('last')}
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default ReusableTable;